import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { httpsCallable } from 'firebase/functions';
import { IconCheck, IconRoute } from '@tabler/icons-react';
import { functions } from '../firebase';
import { getLogoForStyle } from '../utils/styleLogos';
import { groupTemplatesByStyle } from '../components/StylePicker';
import { startStravaConnect } from '../utils/stravaConnect';
import Footer from '../components/Footer';
import MythicMapsLogo from '../assets/logos/80ies_TdF_Logo.png';
import StravaConnectButton from '../assets/strava/btn_strava_connect_with_orange.svg';
import './InviteLandingPage.css';

// Landing page for a referral link (/invite/:inviteId). Shows a preview, connects Strava if
// signed out, then acceptReferralInvite. On success we hand off to ManagePostPage
// (/manage-post/:requestId) which owns the polished generating -> finished-map experience (the
// GenerationRequest's userId is the invitee, so they see it as the owner). An existing account on
// a non-gift invite is blocked and pointed at making their own map.
// Layout mirrors EventLandingPage: full-width hero (the style's example artwork), lockup pill
// (inviter photo x Mythic Maps), value stack, style preview card, official Strava button.
function InviteLandingPage({ user }) {
  const { inviteId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [invite, setInvite] = useState(null);
  const [loadingInvite, setLoadingInvite] = useState(true);
  const [phase, setPhase] = useState('idle'); // idle | accepting | blocked | error
  const [error, setError] = useState('');
  const [styleGroups, setStyleGroups] = useState([]);
  const acceptedRef = useRef(false);

  // Public preview of the invite.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const getInvite = httpsCallable(functions, 'getReferralInvite');
        const res = await getInvite({ inviteId });
        if (!cancelled) { setInvite(res.data); setLoadingInvite(false); }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || t('inviteLanding.loadError', 'This invite link is no longer valid.'));
          setPhase('error');
          setLoadingInvite(false);
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteId]);

  // Best-effort: fetch the public style catalog (same call as the /maps page) to find the
  // gifted style's example artwork for the hero and preview card. Failures degrade silently.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const getTemplates = httpsCallable(functions, 'getPromptTemplates');
        const result = await getTemplates();
        if (cancelled) return;
        const list = Array.isArray(result.data?.templates)
          ? result.data.templates.filter((tpl) => tpl.enabled !== false && !tpl.adminOnly)
          : [];
        setStyleGroups(groupTemplatesByStyle(list));
      } catch (err) {
        // No artwork; the gradient hero and placeholder card cover this.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Once signed in, accept the invite (once), then hand off to ManagePostPage.
  useEffect(() => {
    if (!user || !inviteId || acceptedRef.current) return;
    acceptedRef.current = true;
    setPhase('accepting');
    (async () => {
      try {
        const accept = httpsCallable(functions, 'acceptReferralInvite');
        const res = await accept({ inviteId });
        const requestId = res.data?.requestId;

        if (requestId) {
          // The invitee's +5 is granted server-side when the referral map completes
          // (onReferralGenerationComplete); nothing to do here but hand off.
          navigate(`/manage-post/${requestId}`, { replace: true });
          return;
        }

        // No generation (existing account on a non-gift invite).
        setPhase('blocked');
      } catch (err) {
        console.error('acceptReferralInvite failed:', err);
        setError(err.message || t('inviteLanding.acceptError', 'We could not claim this map.'));
        setPhase('error');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, inviteId, navigate]);

  // Landscape example artwork for the invite's style, preferring the gifted dimension.
  const exampleImageUrl = useMemo(() => {
    if (!invite?.styleName || styleGroups.length === 0) return '';
    const group = styleGroups.find((g) => g.name === invite.styleName);
    if (!group) return '';
    return invite.mapDimension === '3d'
      ? (group.exampleImageUrl3D || group.exampleImageUrl2D || group.exampleImageUrl || '')
      : (group.exampleImageUrl2D || group.exampleImageUrl3D || group.exampleImageUrl || '');
  }, [invite, styleGroups]);

  if (loadingInvite) {
    return (
      <div className="invite-landing-page">
        <div className="invite-landing-message">{t('inviteLanding.loading', 'Loading your invite…')}</div>
        <Footer />
      </div>
    );
  }

  if (phase === 'error' && !invite) {
    return (
      <div className="invite-landing-page">
        <div className="invite-landing-message invite-error">{error}</div>
        <Footer />
      </div>
    );
  }

  const logo = getLogoForStyle(invite.styleName, invite.styleLogoUrl);
  const dimensionLabel = invite.mapDimension === '3d' ? '3D' : '2D';
  const orientationLabel = invite.mapOrientation === 'portrait'
    ? t('inviteLanding.orientationPortrait', 'portrait')
    : t('inviteLanding.orientationLandscape', 'landscape');
  const isGift = invite.isGift === true;
  const inviterName = invite.inviterName || t('inviteLanding.friendFallback', 'A friend');
  const heroStyle = exampleImageUrl ? { backgroundImage: `url(${exampleImageUrl})` } : undefined;

  return (
    <div className="invite-landing-page">
      <div className="invite-hero" style={heroStyle} role="presentation" />

      <div className="invite-lockup">
        {invite.inviterPhoto && (
          <>
            <span className="invite-lockup-chip invite-lockup-avatar">
              <img src={invite.inviterPhoto} alt={inviterName} />
            </span>
            <span className="invite-lockup-x" aria-hidden="true">×</span>
          </>
        )}
        <span className="invite-lockup-chip">
          <img src={MythicMapsLogo} alt="Mythic Maps" />
        </span>
      </div>

      <div className="invite-content">
        <h1 className="invite-title">
          {isGift
            ? t('inviteLanding.titleGift', '{{name}} gifted you a map', { name: inviterName })
            : t('inviteLanding.titleMade', '{{name}} made you a map', { name: inviterName })}
        </h1>
        <p className="invite-sub">
          {t('inviteLanding.subLine', 'A {{style}} {{dimension}} {{orientation}} map, ready to generate on your Strava activity.', {
            style: invite.styleName,
            dimension: dimensionLabel,
            orientation: orientationLabel,
          })}
        </p>

        <ul className="invite-value-stack">
          <li>
            <IconCheck size={17} />
            {t('inviteLanding.valueMap', 'A {{style}} map of your activity', { style: invite.styleName })}
          </li>
          {isGift ? (
            <li>
              <IconCheck size={17} />
              {t('inviteLanding.valueGiftFrom', 'A gift from {{name}}', { name: inviterName })}
            </li>
          ) : (
            <>
              <li>
                <IconCheck size={17} />
                {t('inviteLanding.valueFree', 'Join the app for free')}
              </li>
              <li>
                <IconCheck size={17} />
                {t('inviteLanding.valueDiamonds', 'Plus 5 💎 when your map is ready')}
              </li>
            </>
          )}
        </ul>

        <div className="invite-style-preview">
          <div className="invite-style-preview-card">
            <div className="invite-style-preview-media">
              {exampleImageUrl ? (
                <img
                  src={exampleImageUrl}
                  alt={invite.styleName}
                  loading="lazy"
                  className="invite-style-preview-img"
                />
              ) : (
                <div className="invite-style-preview-placeholder">
                  <IconRoute size={34} />
                </div>
              )}
              {logo && <img src={logo} alt="" className="invite-style-preview-logo" />}
            </div>
            <div className="invite-style-preview-name">{invite.styleName}</div>
          </div>
        </div>

        {!user && (
          <div className="invite-cta">
            <button
              className="invite-strava-connect"
              onClick={() => startStravaConnect(`/invite/${inviteId}`)}
            >
              <img src={StravaConnectButton} alt={t('inviteLanding.connectStrava', 'Connect with Strava')} />
            </button>
          </div>
        )}

        {user && phase === 'accepting' && (
          <div className="invite-status">
            <div className="invite-spinner" />
            <p>{t('inviteLanding.accepting', 'Setting up your map…')}</p>
          </div>
        )}

        {user && phase === 'blocked' && (
          <div className="invite-blocked">
            <p>{t('inviteLanding.blockedExisting', 'It looks like you already have a Mythic Maps account, so this free map is just for new members.')}</p>
            <p>{t('inviteLanding.blockedOwn', 'You can make your own map any time.')}</p>
            <button className="invite-create-btn" onClick={() => navigate('/create')}>
              {t('inviteLanding.makeOwn', 'Make my own map')}
            </button>
          </div>
        )}

        {phase === 'error' && invite && <p className="invite-error">{error}</p>}
      </div>

      <Footer />
    </div>
  );
}

export default InviteLandingPage;
